/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import {
    object, string, func, bool,
} from 'prop-types';
import { useDispatch } from 'react-redux';

import ComponentErrorBoundary from '../../EnterpriseID/Common/ComponentErrorBoundary/ComponentErrorBoundary';
import PersonalizedHomePageCarouselBuilder from '../../GraphqlHomePage/Partials/PersonalizedHomePageCarouselBuilder/PersonalizedHomePageCarouselBuilder';
import noop from '../../../../helpers/noop';
import PersonalizedHomePageGridUIV2 from '../../GraphqlHomePage/Partials/PersonalizedHomePageGridUI/PersonalizedHomePageGridUIV2';
import { emitProductImpression } from '../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';

const TrendingProductRecsBuilder = ({
    data, productsToShow, productClickCallback, isLoading, content, layoutDisplayType,
}) => {
    const dispatch = useDispatch();
    const navPillsUID = content?.tabs_ref?.[0] || null;
    const attributionToken = data?.categoryData?.attributionToken || data?.attributionToken || '';
    const experiments = data?.experimentTracking?.name ? [data?.experimentTracking] : data?.categoryData?.experiments || [];

    useEffect(() => {
        if (!data?.products?.length) {
            return;
        }
        const updatedExperiments = [...experiments];
        if (attributionToken && experiments?.length) {
            experiments?.forEach((exp) => {
                updatedExperiments.push({
                    ...exp,
                    source: 'vertex-ai',
                    attributionToken,
                });
            });
        }
        dispatch(emitProductImpression({
            productsPayload: {
                products: data?.products,
                productPageSize: data?.products?.length,
                mobileBreakpoint: 600,
            },
            page: { type: 'home' },
            experiments: updatedExperiments,
        }));
    }, [data?.products, attributionToken, experiments]);

    const renderCarousel = (title, products, productStyleConfig, containerPadding) => (
        <ComponentErrorBoundary function="PersonalizedHomePageSFContainer" module="PersonalizedContent" message="Something went wrong when displaying Personalized HP Recommendations from Salesforce">
            <PersonalizedHomePageCarouselBuilder
                blockData={{
                    productsToShow: productsToShow || {},
                    title,
                    titleMargin: productStyleConfig?.title_styling?.title_margin,
                    titleFontColor: productStyleConfig?.title_styling?.title_font_color?.color,
                    titleFontSize: productStyleConfig?.title_styling?.title_font_size,
                    titleFontStyle: productStyleConfig?.title_styling?.title_font_style,
                    titleFontFamily: productStyleConfig?.title_styling?.title_font_family,
                    titleFontWeight: productStyleConfig?.title_styling?.title_font_weight,
                    alignTitle: productStyleConfig?.title_styling?.align_title,
                    padding: productStyleConfig?.title_styling?.padding,
                    containerPadding,
                }}
                productClickCallback={productClickCallback}
                products={products}
                demandType="XS"
                productStyleConfig={productStyleConfig}
                navPillsUID={navPillsUID}
                isLoading={isLoading}
                attributionToken={attributionToken}
                dataTestId="homepage-trending-products"
            />
        </ComponentErrorBoundary>
    );

    if (data?.products && data?.products.length > 0) {
        const personalizedCarousel = content?.personalized_carousel;
        const productStylingConfig = content?.personalized_product_card_reference?.length > 0 ? content?.personalized_product_card_reference?.[0] : {};
        const title = productStylingConfig?.main_title || 'Trending Products For You';
        const isGridView = layoutDisplayType === 'GRID';

        const products = data?.products;
        if (isGridView) {
            return (
                <ComponentErrorBoundary function="PersonalizedHPGridUI" module="PersonalizedContent" message="Something went wrong when displaying Personalized HP Recommendations from Salesforce">
                    <PersonalizedHomePageGridUIV2
                        products={products}
                        cmsData={{
                            ...productStylingConfig?.title_styling,
                            title,
                            subTitle: productStylingConfig?.subtitle,
                            min_products_to_show: content?.minimum_products_to_show || 0,
                            padding: personalizedCarousel?.padding,
                        }}
                        isNewDesign={false}
                        handleClickCallback={productClickCallback}
                        productStylingConfig={productStylingConfig}
                        personalizedCarousel={personalizedCarousel}
                        navPillsUID={navPillsUID}
                        attributionToken={attributionToken}
                        experiments={experiments}
                        dataTestId="homepage-trending-products"
                    />
                </ComponentErrorBoundary>
            );
        }
        return renderCarousel(title, products, productStylingConfig, personalizedCarousel?.padding);
    }
    return null;
};

TrendingProductRecsBuilder.propTypes = {
    data: object.isRequired,
    productsToShow: object,
    productClickCallback: func,
    isLoading: bool,
    content: object,
    layoutDisplayType: string,
};

TrendingProductRecsBuilder.defaultProps = {
    productsToShow: {},
    productClickCallback: noop,
    isLoading: false,
    content: {},
    layoutDisplayType: '',
};

export default TrendingProductRecsBuilder;
